import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronDown, Search, MessageSquare,
} from 'lucide-react';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = 'var(--accent-purple)';
const BRAND_DARK = 'var(--accent-text-soft)';

const FAQ_DATA = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'How does Allure work?',
        a: "Allure connects guests with hosts and partner venues. Guests stay with a host (or just join the city), then pay at our partner venues in one tap with their saved card — earning rewards and unlocking perks along the way.",
      },
      {
        q: 'Is Allure free to join?',
        a: 'Yes — creating a guest account is completely free. There are no subscription fees. We earn a small commission on transactions made at our partner venues.',
      },
      {
        q: "I'm on iPhone — how do I get notifications?",
        a: 'On iPhone, Allure runs in Safari and works fully — browsing, payments, QR scanning, everything. To receive real-time push notifications (new reservations, chat messages, host invites), you need to add Allure to your Home Screen first: in Safari, tap the Share button at the bottom of the screen, scroll and tap "Add to Home Screen", then confirm "Add". Open Allure from the new Home Screen icon and allow notifications when prompted. (An App Store version is on our roadmap — in the meantime the Safari version has full functionality.)',
      },
    ],
  },
  {
    category: 'Payments',
    items: [
      {
        q: 'How do I add a payment card?',
        a: 'Open your profile → Payment Method → Add card. We use Viva Wallet (PCI-DSS certified) to securely save your card. You\u2019ll see a temporary €1 hold during setup which is automatically voided.',
      },
      {
        q: 'How do I pay at a venue?',
        a: 'Show your Allure QR at the bar/register. The venue scans it and charges your saved card automatically — no need to re-enter card details, ever.',
      },
      {
        q: 'Why was my card charged €1?',
        a: "That's a temporary hold during card setup to verify your card is real and active. It's automatically voided within minutes and never appears on your statement.",
      },
      {
        q: 'How do I get a refund?',
        a: 'For venue charges, contact us via "Contact Support" with your transaction ID — we coordinate the refund with the venue. Refunds typically land in 3–5 business days.',
      },
    ],
  },
  {
    category: 'Hosts',
    items: [
      {
        q: 'How do I become a host?',
        a: 'Go to your profile and tap "Become a Host". Complete the verification (ID + property details + bank IBAN for payouts), and once approved, you can start hosting guests.',
      },
      {
        q: 'When do hosts get paid?',
        a: 'Host commissions and pool shares are aggregated monthly and automatically transferred to your saved IBAN on the 1st of each month. You\u2019ll see a notification when the payout is on its way.',
      },
      {
        q: 'What does a host earn?',
        a: 'Hosts earn three ways — and the third one is the magic. (1) A base commission on every transaction their guest makes at partner venues. (2) A monthly pool share, unlocked when a host invites 7 or more guests to Allure in the month AND at least 4 of those guests complete a transaction at a partner venue — qualifying hosts share that month\u2019s pool. (3) Direct tips from guests: at the end of the stay (after checkout) guests can send a tip straight to their host through Allure — one tap, no cash, paid out with the host\u2019s next monthly transfer. Exact rates, pool eligibility and tip history live in your Host Dashboard.',
      },
    ],
  },
  {
    category: 'Venues',
    items: [
      {
        q: 'How do venues join Allure?',
        a: 'Create an Allure account, choose "Venue" as your role, and complete our venue registration flow (venue details, photos, opening hours, bank IBAN for payouts). Our team reviews every application within 48 hours and onboards approved venues personally.',
      },
      {
        q: 'Can any venue join Allure?',
        a: "No — Allure is invitation-grade, not open enrolment. Every venue on the platform is hand-picked by our team. We look for outstanding hospitality, a distinctive atmosphere, or a unique experience guests can't get elsewhere. If your venue doesn't meet the bar today, we'll let you know what to work on and we welcome reapplications.",
      },
      {
        q: 'How do venue payouts work?',
        a: 'Every guest payment triggers an instant Viva Bank Transfer to your linked IBAN — no monthly batches, no waiting. SEPA transfers carry a €0.80 fee which is deducted automatically.',
      },
    ],
  },
  {
    category: 'Safety & Trust',
    items: [
      {
        q: 'How do I report a venue or user?',
        a: 'Use the "Report a Venue" option in Support. For urgent safety concerns, contact our team directly at contact@allurexp.com — we treat every report confidentially.',
      },
      {
        q: 'Is my data safe?',
        a: 'Yes. We never store full card numbers (only the last 4 digits + a token), all traffic is encrypted (TLS 1.3), and we comply with GDPR. You can request a data export or full account deletion at any time.',
      },
    ],
  },
];

export default function SupportFaqPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return FAQ_DATA;
    const q = query.toLowerCase();
    return FAQ_DATA
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <div
      className="app-shell"
      data-testid="support-faq-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        backgroundImage:
          'radial-gradient(ellipse 600px 360px at 50% -140px, rgba(196, 18, 48, 0.10) 0%, transparent 60%)',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 390,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '14px 0 0',
        }}
      >
        <button
          onClick={goBack}
          data-testid="support-faq-back-button"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 999,
            cursor: 'pointer',
            padding: 6,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
        </button>
        <NotificationBellButton
          buttonTestId="support-faq-bell-button"
          popupTestId="support-faq-notification-popup"
        />
      </div>

      <RouteTransitionLogo
        width={86}
        height={76}
        dataTestId="support-faq-shared-logo"
        style={{ marginTop: 18 }}
      />
      <h1
        style={{
          fontFamily: FONT,
          fontSize: 26,
          fontWeight: 400,
          color: 'var(--text)',
          margin: '8px 0 0',
        }}
      >
        Frequently Asked
      </h1>
      <p
        style={{
          margin: '6px 0 0',
          fontFamily: SUB_FONT,
          fontSize: 13,
          color: 'var(--text-2)',
          textAlign: 'center',
          maxWidth: 280,
          lineHeight: 1.5,
        }}
      >
        Quick answers to the questions guests, hosts and venues ask most.
      </p>

      {/* Search */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 20,
          position: 'relative',
        }}
      >
        <Search
          size={16}
          color="#7A6E96"
          strokeWidth={2.2}
          style={{ position: 'absolute', left: 14, top: 13 }}
        />
        <input
          data-testid="support-faq-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the help center…"
          style={{
            width: '100%',
            background: 'var(--surface)',
            border: '1px solid rgba(196, 18, 48, 0.08)',
            borderRadius: 14,
            padding: '11px 14px 11px 38px',
            fontFamily: SUB_FONT,
            fontSize: 14,
            outline: 'none',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.08)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* FAQ groups */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {filtered.length === 0 && (
          <p
            data-testid="support-faq-empty"
            style={{
              textAlign: 'center',
              fontFamily: SUB_FONT,
              color: 'var(--text-2)',
              fontSize: 13,
              margin: '24px 0',
            }}
          >
            No matches — try contacting support below.
          </p>
        )}
        {filtered.map((group) => (
          <div key={group.category}>
            <p
              style={{
                margin: '0 0 8px 4px',
                fontFamily: SUB_FONT,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: 'var(--text-2)',
              }}
            >
              {group.category}
            </p>
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid rgba(196, 18, 48, 0.06)',
                borderRadius: 18,
                boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              {group.items.map((item, idx) => {
                const id = `${group.category}-${idx}`;
                const isOpen = openId === id;
                return (
                  <div
                    key={id}
                    style={{
                      borderTop: idx === 0 ? 'none' : '1px solid var(--card-border)',
                    }}
                  >
                    <button
                      data-testid={`support-faq-item-${id.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      onClick={() => {
                        const nextOpen = isOpen ? null : id;
                        setOpenId(nextOpen);
                        // Fire-and-forget click event so admins can see
                        // which FAQ items drive the most engagement.
                        if (nextOpen) {
                          const token = localStorage.getItem('token');
                          axios.post(
                            `${API}/support/faq/event`,
                            { category: group.category, question: item.q },
                            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
                          ).catch(() => {});
                        }
                      }}
                      style={{
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: SUB_FONT,
                          fontSize: 14.5,
                          fontWeight: 700,
                          color: 'var(--text)',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.q}
                      </span>
                      <ChevronDown
                        size={18}
                        color={BRAND}
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 180ms ease',
                          flexShrink: 0,
                        }}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <p
                            style={{
                              margin: 0,
                              padding: '0 16px 14px',
                              fontFamily: SUB_FONT,
                              fontSize: 13.5,
                              color: 'var(--text-2)',
                              lineHeight: 1.55,
                            }}
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 18 }} />

      {/* CTA → contact support */}
      <button
        data-testid="support-faq-contact-cta"
        onClick={() => navigate('/support/contact')}
        style={{
          marginTop: 22,
          width: '100%',
          maxWidth: 360,
          padding: '14px 16px',
          border: 'none',
          borderRadius: 16,
          background: 'var(--accent-purple)',
          color: '#FFFFFF',
          fontFamily: FONT,
          fontSize: 16,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          boxShadow: '0 14px 28px rgba(196, 18, 48, 0.16)',
        }}
      >
        <MessageSquare size={16} strokeWidth={2.2} />
        Still need help? Chat with us
      </button>
    </div>
  );
}
